import React, { Suspense, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { resetPageState } from '../redux/context';
import { selectHistory } from '../redux/history';
import type { HistoryEntry } from '../redux/history';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import type {
  BaseFlowHandle,
  BaseFlowProps,
  GoToTarget,
  PageChangeEvent,
  PageComponent,
  PageHandle,
  PageWithRef,
} from '../types/BaseFlow';
import useLog from '../utilities/useLog';

// ─── Type guard ───────────────────────────────────────────────────────────────

function isHistoryEntry(target: GoToTarget): target is HistoryEntry {
  return typeof target === 'object' && target !== null && 'name' in target && !('then' in target);
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Overarching Flow Structure for the Onboarding Pages
 */
const BaseFlow = React.forwardRef<BaseFlowHandle, BaseFlowProps>(
  (
    { className, finish, getNextPage, getPreviousPage, name, next, onNext, onPageChange, onPrevious, pages, previous },
    ref,
  ) => {
    const log = useLog();
    const dispatch = useAppDispatch();
    const history = useAppSelector(selectHistory);

    const [current, setCurrent] = useState<PageComponent | null>(null);
    const currentPageRef = useRef<PageHandle | null>(null);
    const previousPageRef = useRef<PageHandle | null>(null);
    const getNextPageRef = useRef(getNextPage);
    const getPreviousPageRef = useRef(getPreviousPage);
    const goToRef = useRef<((target: GoToTarget, retry?: () => void) => void) | null>(null);
    const nextRef = useRef<(() => void) | null>(null);
    const previousRef = useRef<(() => void) | null>(null);

    getNextPageRef.current = getNextPage;
    getPreviousPageRef.current = getPreviousPage;

    const currentIndex = useMemo(() => pages.indexOf(current as PageComponent), [current, pages]);
    const previousPage = useMemo(() => history.find((p) => p.name !== currentPageRef.current?.name), [history]);

    // Create the goTo page logic (used by the next/previous to actual navigate to the page)
    const deferredGoTo = useCallback((target: GoToTarget, retry?: () => void) => {
      window.setTimeout(() => goToRef.current!(target, retry));
    }, []);

    goToRef.current = (target, retry) => {
      if (!(typeof target === 'number') && !target) {
        // Do nothing if the target is blank
        return;
      }

      if (target === 'finish') {
        // Flow called for a finish
        finish?.();
        return;
      }
      // TODO referencing the page's displayName is maybe not good
      const getPageIndexByName = (displayName: string) => pages.findIndex((page) => page.displayName === displayName);

      const calculatePageIndex = (page: GoToTarget): number | null => {
        let index: number | null = null;
        if (typeof page === 'number') {
          index = page;
        } else if (isHistoryEntry(page) && page.parent) {
          // This is a page history object, used for back functionality
          index = name === page.parent.name ? getPageIndexByName(page.name) : getPageIndexByName(page.parent.name);
          index = index ?? -1;
        } else if (['function', 'object'].includes(typeof page) && pages.includes(page as PageComponent)) {
          // Going to a page object itself
          index = pages.indexOf(page as PageComponent);
        } else if (typeof page === 'string') {
          // Going to a page by its name
          index = getPageIndexByName(page);
        }
        return index;
      };

      // Go to the target once it has resolved if it is a promise
      if (typeof (target as Promise<GoToTarget>)?.then === 'function') {
        (target as Promise<GoToTarget>).then((res) => {
          const index = calculatePageIndex(res);
          if (target === 'finish' || typeof index === 'number') {
            // A known page was returned. Go to it.
            deferredGoTo(res, retry);
          } else {
            // This result isn't recognized as a page. Retry.
            window.setTimeout(() => {
              // Defer to let redux update
              retry?.();
            });
          }
        });
        return;
      }

      const index = calculatePageIndex(target);
      if (typeof index !== 'number') {
        log.warn(`%c${name} >> No page found matching:`, 'color: aqua', target);
        return;
      }

      // Going back if the target index is < 0
      if (index < 0) {
        log.debug(`${name} >> flow-previous ${index}`, (target as PageHandle)?.name);
        previous?.();
        return;
      }

      // This flow is done, trigger the next on the parent flow
      if (index >= pages.length) {
        log.debug(`${name} >> Finishing ${index} of ${pages.length}`);
        next?.();
        return;
      }

      // Navigate to a page within this flow
      const nextPage = pages[index];
      log.debug(name, '>> goTo:', index, nextPage.displayName);

      // Keep a handle on the previous page's ref
      if (isHistoryEntry(target) && target.parent) {
        // Retain the history element's previous page
        previousPageRef.current = target.previousPage ?? null;
      } else if (currentPageRef.current) {
        // Pass a handle on the previous page before we change it
        previousPageRef.current = currentPageRef.current.viewable
          ? currentPageRef.current
          : (currentPageRef.current?.getCurrentPage?.() ?? null);
      }

      // Reset the general page state (i.e. backable & header visibility)
      if (currentPageRef.current) {
        dispatch(resetPageState());
      }

      // Go to the next page
      setCurrent(nextPage);
    };

    // Create the next page logic
    const deferredNext = useCallback(() => {
      window.setTimeout(() => {
        // Defer to let redux updates flush
        nextRef.current!();
      });
    }, []);
    nextRef.current = () => {
      const nextIndex = currentIndex + 1;
      const target = getNextPageRef.current ? getNextPageRef.current(current, nextIndex) : nextIndex;
      onNext?.(currentPageRef.current);
      deferredGoTo(target, () => deferredNext());
    };

    // Create the previous page logic
    const deferredPrevious = useCallback(() => {
      window.setTimeout(() => {
        // Defer to let redux updates flush
        previousRef.current!();
      });
    }, []);
    previousRef.current = () => {
      // Notify the current page of the previous, allowing it to cancel
      const event = new Event('previous', { cancelable: true });
      currentPageRef.current?.onPrevious?.(event);

      const prev = previousPage; // Use the provided page if we came back from a child flow
      if (!event.defaultPrevented && (getPreviousPageRef.current || prev?.replayable)) {
        // Go back if the event wasn't canceled
        const target = getPreviousPageRef.current ? getPreviousPageRef.current(current, prev) : prev;
        onPrevious?.(currentPageRef.current, target);
        deferredGoTo(target, () => deferredPrevious());
      }
    };

    const skip = useCallback(() => {
      // Notify the current page of the previous, allowing it to cancel
      const event = new Event('skip', { cancelable: true });
      currentPageRef.current?.onSkip?.(event);

      if (!event.defaultPrevented) {
        // Go forward if the event wasn't canceled
        deferredNext();
      }
    }, [deferredNext]);

    const handlePageChange = useCallback(
      (page: PageChangeEvent) => {
        // Bubble up the page change, inserting this flow's previous page if the event doesn't have one
        onPageChange?.({
          ...page,
          previousPage: page.previousPage ?? previousPageRef.current,
        });
      },
      [onPageChange],
    );

    useImperativeHandle(
      ref,
      () => ({
        className,
        current,
        currentIndex,
        getCurrentPage: () => {
          let page: PageHandle | null | undefined = currentPageRef.current;
          while (!page?.viewable) {
            page = page?.getCurrentPage?.();
            if (!page) break;
          }

          return page
            ? {
                ...page,
                parent: ref && 'current' in ref ? ref.current : null,
              }
            : null;
        },
        goTo: deferredGoTo,
        name,
        next: deferredNext,
        previous: deferredPrevious,
        previousPage: previousPageRef.current?.getCurrentPage?.() || previousPageRef.current,
        skip,
      }),
      [current, currentIndex, deferredGoTo, deferredNext, deferredPrevious, className, name, ref, skip],
    );

    // Effects
    useEffect(() => {
      // Automatically go to the next (first) page on mount
      deferredNext();
    }, [deferredNext]);

    useEffect(() => {
      if (current) {
        window.setTimeout(() => {
          handlePageChange({
            ...(currentPageRef.current ?? {}),
            index: currentIndex,
            parent: ref && 'current' in ref ? ref.current : null,
            previousPage: previousPageRef.current,
          });
        });
      }
    }, [current, currentIndex, handlePageChange, ref]);

    // Render
    log.debug(name, '>> render', current?.displayName);

    if (!current) {
      return null;
    }

    const Page = current as unknown as PageWithRef;

    return (
      <Suspense fallback={<div />}>
        <Page
          finish={finish}
          goTo={deferredGoTo}
          next={deferredNext}
          onNext={onNext}
          onPageChange={handlePageChange}
          onPrevious={onPrevious}
          parent={ref && 'current' in ref ? ref.current : null}
          previous={deferredPrevious}
          ref={currentPageRef}
          skip={skip}
        />
      </Suspense>
    );
  },
);

BaseFlow.displayName = 'BaseFlow';

export default BaseFlow;
